const AMPLITUDE_HTTP_API_URL = 'https://api2.amplitude.com/2/httpapi';
const AMPLITUDE_HTTP_MAX_ATTEMPTS = 3;
const AMPLITUDE_HTTP_RETRY_DELAYS_MS = [100, 250] as const;

// Simple UUID generator for browser
const generateUUID = () => {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (char) {
		const r = Math.random() * 16 | 0, v = char === 'x' ? r : (r & 0x3 | 0x8);
		return v.toString(16);
	});
};

// Storage keys and user ID
import { STORAGE_KEYS, postWebkitMessage } from '../../shared-utils';
import { getAndStoreCurrentUserId } from './user';

export class AnalyticsClient {
	private apiKey: string;
	private userId: string | null = null;
	private deviceId: string | null = null;
	private sessionId: number | null = null;
	private hasGivenUpResolution = false;
	private resolveRetryCount = 0;
	private readonly MAX_RETRIES = 20;

	constructor(apiKeyOverride?: string) {
		const apiKey =
			apiKeyOverride ?? process.env.BUN_PUBLIC_AMPLITUDE_API_KEY;
		// const apiKey = '';
		const isProd = process.env.NODE_ENV === 'production';

		if (!apiKey && isProd) {
			throw new Error('[AnalyticsClient] CRITICAL: BUN_PUBLIC_AMPLITUDE_API_KEY is missing in production build!');
		}

		this.apiKey = apiKey || '';
		console.log('[AnalyticsClient] Initialized');
	}

	init() {
		if (typeof window === 'undefined') return;

		if (!this.apiKey) {
			console.warn('Amplitude Analytics: No API Key found (BUN_PUBLIC_AMPLITUDE_API_KEY). Analytics tracking will be disabled.');
		}

		// Initialize IDs
		this.getDeviceId();

		// Start resolution
		this.tryResolveUserId();

		// Start session
		this.getSessionId();
	}

	setUserId(userId: string) {
		if (typeof window === 'undefined') return;
		this.userId = userId;
		this.hasGivenUpResolution = false;
		this.resolveRetryCount = 0;
	}

	private async tryResolveUserId() {
		if (this.userId || this.hasGivenUpResolution) {
			this.resolveRetryCount = 0;
			return;
		}

		// Use the unified getAndStoreCurrentUserId from shared-api
		const userId = await getAndStoreCurrentUserId();

		if (userId) {
			this.setUserId(userId);
			return;
		}

		if (this.resolveRetryCount >= this.MAX_RETRIES) {
			console.warn('[Analytics] Failed to resolve user ID after retries. Continuing with anonymous device analytics.');
			this.hasGivenUpResolution = true;
			return;
		}

		this.resolveRetryCount++;
		const delay = Math.min(200 * Math.pow(1.5, this.resolveRetryCount), 5000);

		await new Promise(resolve => setTimeout(resolve, delay));
		await this.tryResolveUserId();
	}

	private getDeviceId(): string {
		if (this.deviceId) return this.deviceId;
		if (typeof window === 'undefined') return 'server-side';

		let stored = window.localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
		if (!stored) {
			stored = generateUUID();
			window.localStorage.setItem(STORAGE_KEYS.DEVICE_ID, stored);
		}
		this.deviceId = stored;
		return this.deviceId;
	}

	private getSessionId(): number {
		if (typeof window === 'undefined') return Date.now();

		const now = Date.now();
		const lastEventTime = Number(
			window.localStorage.getItem(STORAGE_KEYS.LAST_EVENT_TIME) || 0,
		);
		const THIRTY_MINUTES = 30 * 60 * 1000;

		let currentSessionId = Number(
			window.localStorage.getItem(STORAGE_KEYS.SESSION_ID),
		);

		if (!currentSessionId || (now - lastEventTime > THIRTY_MINUTES)) {
			currentSessionId = now;
			window.localStorage.setItem(
				STORAGE_KEYS.SESSION_ID,
				String(currentSessionId),
			);
		}

		this.sessionId = currentSessionId;
		return this.sessionId;
	}

	private updateLastEventTime() {
		if (typeof window !== 'undefined') {
			window.localStorage.setItem(
				STORAGE_KEYS.LAST_EVENT_TIME,
				String(Date.now()),
			);
		}
	}

	private async sendToAmplitude(event: any) {
		if (!this.apiKey) return;
		if (typeof window === 'undefined') return;

		// Amplitude accepts device-only events. Sending immediately keeps public
		// guest flows observable; later events add user_id while retaining this
		// device_id so Amplitude can associate the anonymous history.
		// Refresh session if needed
		const sessionId = this.getSessionId();
		this.updateLastEventTime();

		const payload = {
			api_key: this.apiKey,
			events: [{
				...event,
				...(this.userId ? { user_id: this.userId } : {}),
				device_id: this.getDeviceId(),
				session_id: sessionId,
				time: event.time || Date.now(),
				platform: 'Web',
				os_name: 'Web',
				os_version: navigator.userAgent,
				library: 'http-api-v2'
			}]
		};

		for (let attempt = 0; attempt < AMPLITUDE_HTTP_MAX_ATTEMPTS; attempt++) {
			try {
				const response = await fetch(AMPLITUDE_HTTP_API_URL, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'Accept': '*/*'
					},
					body: JSON.stringify(payload)
				});
				if (response.ok) {
					return;
				}

				const isRetryable =
					response.status === 429 || response.status >= 500;
				if (!isRetryable || attempt === AMPLITUDE_HTTP_MAX_ATTEMPTS - 1) {
					console.error(
						`Amplitude track failed with HTTP ${response.status}`,
					);
					return;
				}
			} catch (error) {
				// A network failure may happen after Amplitude accepted the
				// request, so retry only explicit 429/5xx responses where the
				// server tells us the event was not accepted.
				console.error('Amplitude track failed:', error);
				return;
			}

			await new Promise((resolve) =>
				setTimeout(
					resolve,
					AMPLITUDE_HTTP_RETRY_DELAYS_MS[attempt] ??
						AMPLITUDE_HTTP_RETRY_DELAYS_MS.at(-1) ??
						0,
				),
			);
		}
	}

	trackEvent(
		eventType: string,
		eventProperties?: Record<string, any>,
	): Promise<void> {
		// Use native iOS bridge when available for unified session tracking
		const sent = postWebkitMessage('logAmplitudeEvent', {
			eventName: eventType,
			eventProperties: eventProperties ?? {}
		});

		if (!sent) {
			return this.sendToAmplitude({
				event_type: eventType,
				event_properties: eventProperties,
				time: Date.now()
			});
		}

		return Promise.resolve();
	}

	setUserProperties(properties: Record<string, any>): Promise<void> {
		return this.sendToAmplitude({
			event_type: '$identify',
			user_properties: {
				$set: properties
			},
			time: Date.now()
		});
	}
}

// Export singleton instance
export const analyticsClient = new AnalyticsClient();

// Backward compatibility exports
export const initAnalytics = () => analyticsClient.init();
export const setUserId = (userId: string) => analyticsClient.setUserId(userId);
export const trackEvent = (eventType: string, eventProperties?: Record<string, any>) =>
	analyticsClient.trackEvent(eventType, eventProperties);
export const setUserProperties = (properties: Record<string, any>) =>
	analyticsClient.setUserProperties(properties);


export const trackPageView = (path: string) => {
	trackEvent('view_games_page', {
		path: path,
		title: document.title,
		url: window.location.href
	});
};

export const trackGameStartEvent = (
	gameName: string,
	gameSessionId: string,
	gameIndex?: number,
	extraProperties?: Record<string, any>
) => {
	trackEvent('game_start', {
		...(extraProperties ?? {}),
		game_name: gameName,
		game_session_id: gameSessionId,
		game_index: gameIndex
	});
};

export const trackGameEndEvent = (
	gameName: string,
	gameSessionId: string,
	score: number | undefined,
	success: boolean,
	gameIndex?: number,
	extraProperties?: Record<string, any>
) => {
	trackEvent('game_end', {
		...(extraProperties ?? {}),
		game_name: gameName,
		game_session_id: gameSessionId,
		score: score,
		success: success,
		game_index: gameIndex
	});
};

export const trackGamePauseEvent = (gameName: string, gameSessionId: string) => {
	trackEvent('game_pause', { game_name: gameName, game_session_id: gameSessionId });
};

export const trackGameResumeEvent = (gameName: string, gameSessionId: string) => {
	trackEvent('game_resume', { game_name: gameName, game_session_id: gameSessionId });
};

export const trackGamePenaltyEvent = (gameName: string, gameSessionId: string, penaltyMs: number) => {
	trackEvent('game_penalty', {
		game_name: gameName,
		game_session_id: gameSessionId,
		penalty_ms: penaltyMs
	});
};

export const trackLeaderboardOpen = (gameName: string) => {
	trackEvent('leaderboard_open', { game_name: gameName });
};

export const trackLeaderboardScroll = (gameName: string) => {
	trackEvent('leaderboard_scrolled', { game_name: gameName });
};

export const trackShare = (contentParams: Record<string, any>) => {
	trackEvent('share_clicked', contentParams);
};
export const trackNextGameClick = (fromGame: string, toGame: string) => {
	trackEvent('next_game_clicked', {
		from_game: fromGame,
		to_game: toGame
	});
};

export type YaknessPage = 'preview' | 'play' | 'matchup' | 'bracket';
export type YaknessTab = 'my_path' | 'full_bracket' | 'campus_cup';
export type YaknessModal = 'how_it_works' | 'post_match' | 'post_to_feed_sheet';

export interface YaknessContext {
	yakness_page?: YaknessPage;
	yakness_tab?: YaknessTab;
	yakness_path?: string;
	yakness_season_id?: string;
	yakness_round_id?: string;
	yakness_competition_mode?: string;
	yakness_match_status?: string;
	yakness_match_no?: number | null;
	group_id?: string;
	group_name?: string;
	yakness_my_school_analytics_name?: string;
	yakness_opponent_school_id?: string;
	yakness_opponent_school_name?: string;
	yakness_opponent_school_analytics_name?: string;
	yakness_is_internal?: boolean;
}

const buildYaknessProperties = (
	context: YaknessContext = {},
	extraProperties: Record<string, any> = {}
) => ({
	...extraProperties,
	...context,
	experience: 'yakness',
});

export const trackYaknessEvent = (
	eventType: string,
	context: YaknessContext = {},
	extraProperties?: Record<string, any>
) => {
	trackEvent(eventType, buildYaknessProperties(context, extraProperties));
};

export const trackYaknessPageView = (
	page: YaknessPage,
	context: YaknessContext = {},
	extraProperties?: Record<string, any>
) => {
	trackYaknessEvent('yakness_page_view', {
		...context,
		yakness_page: page
	}, extraProperties);
};

export const trackYaknessTabView = (
	tab: YaknessTab,
	context: YaknessContext = {},
	extraProperties?: Record<string, any>
) => {
	trackYaknessEvent('yakness_tab_view', {
		...context,
		yakness_tab: tab
	}, extraProperties);
};

export const trackYaknessNavClick = (
	context: YaknessContext = {},
	extraProperties?: Record<string, any>
) => {
	trackYaknessEvent('yakness_nav_click', context, extraProperties);
};

export const trackYaknessRunSubmitStarted = (
	context: YaknessContext = {},
	extraProperties?: Record<string, any>
) => {
	trackYaknessEvent('yakness_run_submit_started', context, extraProperties);
};

export const trackYaknessRunSubmitSucceeded = (
	context: YaknessContext = {},
	extraProperties?: Record<string, any>
) => {
	trackYaknessEvent('yakness_run_submit_succeeded', context, extraProperties);
};

export const trackYaknessRunSubmitFailed = (
	context: YaknessContext = {},
	extraProperties?: Record<string, any>
) => {
	trackYaknessEvent('yakness_run_submit_failed', context, extraProperties);
};

export const trackYaknessRunSubmitSkipped = (
	context: YaknessContext = {},
	extraProperties?: Record<string, any>
) => {
	trackYaknessEvent('yakness_run_submit_skipped', context, extraProperties);
};

export const trackYaknessModalView = (
	modal: YaknessModal,
	context: YaknessContext = {},
	extraProperties?: Record<string, any>
) => {
	trackYaknessEvent('yakness_modal_view', context, {
		...(extraProperties ?? {}),
		yakness_modal: modal,
	});
};

export const trackYaknessModalAction = (
	modal: YaknessModal,
	action: string,
	context: YaknessContext = {},
	extraProperties?: Record<string, any>
) => {
	trackYaknessEvent('yakness_modal_action', context, {
		...(extraProperties ?? {}),
		yakness_modal: modal,
		yakness_modal_action: action,
	});
};

export const trackYaknessSchoolContextMissing = (
	context: YaknessContext = {},
	reason: string = 'missing_group_id',
	extraProperties?: Record<string, any>
) => {
	trackYaknessEvent('yakness_school_context_missing', context, {
		reason,
		...extraProperties
	});
};
